/**
 * Activity category facet tree (TRAV23 activities-front merge).
 *
 * Unlike the other filters, activity categories are hierarchical and single-select: the
 * search API takes at most one category key and returns the filtered subtree, so this
 * renders radio-like rows rather than a checkbox group.
 *
 * Expansion is local state keyed by category id. It is deliberately not persisted to
 * the store or URL: which branches are open is a view detail, and the API replaces the
 * tree wholesale on every filter change.
 */

import React, { useCallback, useState } from 'react';

import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';

import type { IActivityCategoryCounterItem } from '@common-types';

import './style.scss';

const INDENT_PER_DEPTH_PX = 16;
const ZERO = 0;

interface IProps {
  titleId: string;
  nodes: IActivityCategoryCounterItem[] | undefined;
  selectedKey: string;
  onSelect: (key: string) => void;
  disabled?: boolean;
}

interface ICategoryRowsProps {
  nodes: IActivityCategoryCounterItem[];
  depth: number;
  expanded: Set<string>;
  toggle: (key: string) => void;
  selectedKey: string;
  onSelect: (key: string) => void;
  disabled?: boolean;
}

function CategoryRows({
  nodes,
  depth,
  expanded,
  toggle,
  selectedKey,
  onSelect,
  disabled,
}: ICategoryRowsProps): React.ReactElement {
  const intl = useIntl();

  return (
    <>
      {nodes.map((node) => {
        const id = String(node.key);
        // `name` is the current field; `text` is the older one some suppliers still send.
        const label = node.name || node.text || id;
        const hasChildren = Boolean(node.children?.length);
        const isOpen = expanded.has(id);
        const isSelected = selectedKey === id;

        return (
          <div key={id} className="filter-category-tree__node">
            <div
              className={`filter-category-tree__row ${
                isSelected ? 'filter-category-tree__row--selected' : ''
              }`}
              style={{ paddingLeft: depth * INDENT_PER_DEPTH_PX }}
            >
              <button
                type="button"
                className="filter-category-tree__main"
                disabled={disabled || node.disabled}
                onClick={() => onSelect(id)}
              >
                <span className="filter-category-tree__label-text">{label}</span>
              </button>

              {hasChildren ? (
                <button
                  type="button"
                  className="filter-category-tree__chevron"
                  disabled={disabled || node.disabled}
                  onClick={(event) => {
                    // Expanding a branch must not also select it.
                    event.stopPropagation();
                    toggle(id);
                  }}
                  aria-expanded={isOpen}
                  aria-label={intl.formatMessage({
                    id: isOpen ? 'activity.filter.category.collapse' : 'activity.filter.category.expand',
                  })}
                >
                  {isOpen ? <UpOutlined /> : <DownOutlined />}
                </button>
              ) : (
                <span className="filter-category-tree__chevron-spacer" aria-hidden />
              )}

              {node.count != null && node.count > ZERO && (
                <span className="filter-category-tree__count">{node.count}</span>
              )}
            </div>

            {hasChildren && isOpen && node.children && (
              <CategoryRows
                nodes={node.children}
                depth={depth + 1}
                expanded={expanded}
                toggle={toggle}
                selectedKey={selectedKey}
                onSelect={onSelect}
                disabled={disabled}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export const FilterCategoryTree: React.FC<IProps> = ({
  titleId,
  nodes,
  selectedKey,
  onSelect,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  if (!nodes?.length) {
    return null;
  }

  return (
    <div className="filter filter-category-tree exclude-border">
      <p className="filter__title secondary-font">
        <FormattedMessage id={titleId} />
      </p>

      <CategoryRows
        nodes={nodes}
        depth={ZERO}
        expanded={expanded}
        toggle={toggle}
        selectedKey={selectedKey}
        onSelect={onSelect}
        disabled={disabled}
      />
    </div>
  );
};
